import { useEffect, useMemo, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Sparkles, Ticket, MessageSquare, Copy, Bug, Star, Users, LineChart as LineChartIcon,
  BarChart3, ShieldAlert, ShieldOff, Search, Activity, Award, MessageCircle, Rocket, ChevronLeft, ChevronRight,
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Textarea } from "@/components/ui/textarea";

const chartAxis = { fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: "hsl(var(--muted-foreground))" };

function StatCard({ label, value, icon: Icon, tone = "primary" }) {
  return (
    <div className={`rounded-2xl border p-5 ${tone === "primary" ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
      <div className="label-mono">{label}</div>
      <div className="mt-2 flex items-end justify-between">
        <div className="font-display text-3xl font-black tracking-tighter">{value}</div>
        {Icon && <Icon className={`h-5 w-5 ${tone === "primary" ? "text-primary" : "text-accent"}`} />}
      </div>
    </div>
  );
}

function LearnersTab() {
  const [data, setData] = useState({ users: [], total: 0 });
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const params = new URLSearchParams({ limit, skip });
    if (q.trim()) params.set("q", q.trim());
    const { data } = await api.get(`/admin/users?${params}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);

  const toggleDisable = async (u) => {
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/disable`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Learner enabled" : "Learner disabled");
      await load();
    } catch { toast.error("Could not update"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())} placeholder="Search by name or email…" className="h-9 w-72" data-testid="admin-users-search" />
          <Button onClick={() => { setSkip(0); load(); }} size="sm" variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">Search</Button>
        </div>
        <div className="label-mono">{data.total} learners</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border label-mono text-left">
              <th className="pb-2 pr-4">Learner</th>
              <th className="pb-2 pr-4">Plan</th>
              <th className="pb-2 pr-4">Level</th>
              <th className="pb-2 pr-4">Streak</th>
              <th className="pb-2 pr-4">Lessons</th>
              <th className="pb-2 pr-4">Certs</th>
              <th className="pb-2 pr-4">Sessions</th>
              <th className="pb-2 pr-4">Joined</th>
              <th className="pb-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className={`border-b border-border/60 ${u.disabled ? "opacity-50" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                <td className="py-3 pr-4">
                  <div className="font-medium truncate max-w-[240px]">{u.name || "—"}</div>
                  <div className="label-mono truncate max-w-[240px]">{u.email}</div>
                </td>
                <td className="pr-4"><span className="rounded-full border border-border px-2 py-0.5 font-mono text-[0.6rem] uppercase">{u.plan || "free"}</span></td>
                <td className="pr-4 font-mono">L{u.level || 1}</td>
                <td className="pr-4 font-mono">{u.streak || 0}</td>
                <td className="pr-4 font-mono">{u.completed_lessons_count}</td>
                <td className="pr-4 font-mono">{u.certificate_count}</td>
                <td className="pr-4 font-mono">{u.sessions_count}</td>
                <td className="pr-4 label-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === u.id}
                    onClick={() => toggleDisable(u)}
                    className="rounded-full font-mono text-[0.6rem] uppercase tracking-widest"
                    data-testid={`admin-toggle-user-${u.id}`}
                  >
                    {u.disabled ? <><ShieldAlert className="mr-1 h-3 w-3" /> Enable</> : <><ShieldOff className="mr-1 h-3 w-3" /> Disable</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="label-mono">Showing {data.users.length} · page {Math.floor(skip / limit) + 1}</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="rounded-full"><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="rounded-full"><ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/platform-analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="label-mono animate-pulse">Loading analytics…</div>;

  const feedbackData = Object.entries(data.feedback.by_kind || {}).map(([k, v]) => ({ kind: k, count: v }));

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Total learners" value={data.learners.total} icon={Users} />
        <StatCard label="Active (7d)" value={data.engagement.wau} icon={Activity} tone="primary" />
        <StatCard label="Weekly retention" value={`${data.engagement.weekly_retention_pct}%`} icon={LineChartIcon} />
        <StatCard label="Certificates" value={data.certificates.total} icon={Award} />
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="DAU" value={data.engagement.dau} tone="card" icon={Activity} />
        <StatCard label="Learning sessions" value={data.ai.sessions_total} tone="card" icon={MessageCircle} />
        <StatCard label="Mentor messages" value={data.ai.messages_total} tone="card" icon={MessageSquare} />
        <StatCard label="Referrals" value={data.referrals.total} tone="card" icon={Rocket} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 label-mono">// signups · last 30 days</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.signups_last_30d}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={chartAxis} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={chartAxis} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="signups" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 label-mono">// feedback by kind</div>
          {feedbackData.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No feedback yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={feedbackData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="kind" tick={chartAxis} />
                <YAxis tick={chartAxis} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="count" fill="#CCFF00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 label-mono">// most popular learning paths</div>
        {(data.paths || []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">No enrollments yet.</div>
        ) : (
          <ul className="space-y-2">
            {data.paths.map((p) => (
              <li key={p.slug} className="flex items-center gap-3 rounded-lg border border-border p-3">
                <div className="grid h-9 w-9 place-items-center rounded-md font-mono text-xs" style={{ backgroundColor: `${p.color}22`, color: p.color, border: `1px solid ${p.color}55` }}>
                  {p.title[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{p.title}</div>
                  <div className="label-mono">{p.enrollments} enrolled · {p.completions} completed · {p.avg_completion_pct}% avg</div>
                </div>
                <div className="h-2 w-32 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${p.avg_completion_pct}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function HealthTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/system-health").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="label-mono animate-pulse">Loading…</div>;
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-health-tab">
      <div className="flex items-center gap-2">
        <div className={`h-3 w-3 rounded-full ${data.mongo ? "bg-accent" : "bg-destructive"}`} />
        <div className="font-display text-xl font-bold">MongoDB {data.mongo ? "healthy" : "unreachable"}</div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Object.entries(data.collections).map(([name, count]) => (
          <div key={name} className="rounded-xl border border-border bg-background p-3">
            <div className="label-mono">{name}</div>
            <div className="mt-1 font-display text-2xl font-black">{count}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 label-mono">last check · {new Date(data.ts).toLocaleString()}</div>
    </div>
  );
}

function FeedbackTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ kind: "", status: "", priority: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [notifyUser, setNotifyUser] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const { data } = await api.get(`/admin/feedback?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load feedback"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const openItem = async (f) => {
    setSelected(f); setDetail(null); setNote(""); setNotifyUser(false);
    try {
      const { data } = await api.get(`/admin/feedback/${f.feedback_id}`);
      setDetail(data);
    } catch { setDetail(f); }
  };

  const patch = async (updates) => {
    if (!selected) return;
    setBusy(true);
    try {
      const body = { ...updates };
      if (note.trim()) body.internal_note = note.trim();
      if (notifyUser) body.notify_user = true;
      const { data } = await api.put(`/admin/feedback/${selected.feedback_id}`, body);
      setDetail(data);
      setItems((prev) => prev.map((x) => x.feedback_id === data.feedback_id ? { ...x, ...data } : x));
      setNote("");
      setNotifyUser(false);
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const iconFor = (kind) => kind === "bug" ? Bug : kind === "feature" ? Sparkles : kind === "rating" ? Star : kind === "lesson" ? MessageCircle : MessageSquare;
  const priorityTone = (p) => p === "urgent" ? "bg-destructive/20 text-destructive border-destructive/40"
                          : p === "high" ? "bg-accent/20 text-accent border-accent/40"
                          : p === "medium" ? "bg-primary/10 text-primary border-primary/40"
                          : "bg-secondary text-muted-foreground border-border";
  const statusTone = (s) => s === "fixed" ? "bg-accent/20 text-accent"
                        : s === "in_progress" ? "bg-primary/20 text-primary"
                        : s === "closed" ? "bg-secondary text-muted-foreground"
                        : "bg-destructive/20 text-destructive";

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
      <div data-testid="admin-feedback-list" className="rounded-2xl border border-border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search email or message…" className="h-9 w-56" data-testid="admin-feedback-search" />
          <select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} data-testid="admin-feedback-filter-kind" className="h-9 rounded-md border border-border bg-background px-2 text-xs font-mono uppercase tracking-widest">
            <option value="">All kinds</option><option value="bug">Bug</option><option value="feature">Feature</option><option value="lesson">Lesson</option><option value="rating">Rating</option><option value="general">General</option>
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="admin-feedback-filter-status" className="h-9 rounded-md border border-border bg-background px-2 text-xs font-mono uppercase tracking-widest">
            <option value="">All statuses</option><option value="new">New</option><option value="in_progress">In progress</option><option value="fixed">Fixed</option><option value="closed">Closed</option>
          </select>
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="admin-feedback-filter-priority" className="h-9 rounded-md border border-border bg-background px-2 text-xs font-mono uppercase tracking-widest">
            <option value="">All priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <div className="ml-auto label-mono">{total} total</div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No feedback matching filters.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((f) => {
              const Icon = iconFor(f.kind);
              const isSel = selected?.feedback_id === f.feedback_id;
              return (
                <li key={f.feedback_id}>
                  <button
                    onClick={() => openItem(f)}
                    data-testid={`admin-feedback-item-${f.feedback_id}`}
                    className={`w-full text-left rounded-xl border p-3 transition ${isSel ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid h-8 w-8 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest ${priorityTone(f.priority || "low")}`}>{f.priority || "low"}</span>
                          <span className={`rounded-full px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest ${statusTone(f.status || "new")}`}>{(f.status || "new").replace("_", " ")}</span>
                          <span className="label-mono">{f.kind}</span>
                          <span className="label-mono truncate">{f.user_email}</span>
                          {f.rating && <span className="label-mono">{f.rating}/5</span>}
                        </div>
                        <div className="mt-1 text-sm line-clamp-2">{f.message}</div>
                        <div className="mt-1 label-mono">{new Date(f.ts).toLocaleString()}{f.page ? ` · ${f.page}` : ""}</div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <aside className="rounded-2xl border border-border bg-card p-4 h-fit sticky top-6">
        {!selected ? (
          <div className="label-mono text-center py-6">// select an item to triage</div>
        ) : (
          <div data-testid="admin-feedback-detail" className="space-y-3">
            <div className="label-mono">// triage · {selected.feedback_id.slice(0, 8)}</div>
            <div className="font-display font-bold">{selected.kind} feedback</div>
            <div className="label-mono truncate">{selected.user_email}{selected.user_name ? ` · ${selected.user_name}` : ""}</div>
            <div className="rounded-xl border border-border bg-background p-3 text-sm whitespace-pre-wrap">{selected.message}</div>
            <div className="grid grid-cols-2 gap-2 label-mono">
              {selected.page && <div>page · {selected.page}</div>}
              {selected.lesson_slug && <div>lesson · {selected.lesson_slug} L{(selected.lesson_index ?? 0) + 1}</div>}
              {selected.viewport && <div>viewport · {selected.viewport}</div>}
              {selected.device && <div>device · {selected.device}</div>}
              {selected.user_agent && <div className="col-span-2 truncate">ua · {selected.user_agent}</div>}
            </div>

            {detail?.screenshot && (
              <div>
                <div className="label-mono mb-1">// screenshot</div>
                <img alt="screenshot" src={detail.screenshot} className="w-full rounded-lg border border-border" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <select
                value={selected.priority || "low"}
                onChange={(e) => patch({ priority: e.target.value })}
                data-testid="admin-feedback-priority-select"
                className="h-9 rounded-md border border-border bg-background px-2 text-xs font-mono uppercase tracking-widest"
              >
                <option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
              <select
                value={selected.status || "new"}
                onChange={(e) => patch({ status: e.target.value })}
                data-testid="admin-feedback-status-select"
                className="h-9 rounded-md border border-border bg-background px-2 text-xs font-mono uppercase tracking-widest"
              >
                <option value="new">New</option><option value="in_progress">In progress</option><option value="fixed">Fixed</option><option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <div className="label-mono">// internal note</div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-feedback-note-input" placeholder="Note for the team (also included in resolution email if you notify)" className="mt-1 min-h-[70px] rounded-lg" />
              <label className="mt-2 flex items-center gap-2 label-mono cursor-pointer">
                <input type="checkbox" checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} data-testid="admin-feedback-notify-toggle" />
                notify user when I mark fixed/closed
              </label>
              <Button
                onClick={() => patch({})}
                disabled={busy || (!note.trim() && !notifyUser)}
                size="sm"
                className="mt-2 rounded-full btn-neon font-mono text-xs uppercase tracking-widest w-full"
                data-testid="admin-feedback-save-note"
              >
                Save note
              </Button>
            </div>

            {(detail?.internal_notes?.length || 0) > 0 && (
              <div>
                <div className="label-mono">// history</div>
                <ul className="mt-1 space-y-2">
                  {detail.internal_notes.map((n, i) => (
                    <li key={i} className="rounded-lg border border-border bg-background p-2 text-xs">
                      <div className="label-mono">{n.admin_name} · {new Date(n.ts).toLocaleString()}</div>
                      <div className="mt-1 whitespace-pre-wrap">{n.note}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

// Existing tabs (Invites + Feedback) preserved inside this component
function OpsTab() {
  const [invites, setInvites] = useState({ invites: [], gating_enabled: false });
  const [feedback, setFeedback] = useState([]);
  const [settings, setSettings] = useState(null);
  const [count, setCount] = useState(5);
  const [emailsInput, setEmailsInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [i, f, s] = await Promise.all([
        api.get("/admin/invites"),
        api.get("/admin/feedback"),
        api.get("/admin/beta-settings"),
      ]);
      setInvites(i.data); setFeedback(f.data?.items || []); setSettings(s.data);
    } catch { toast.error("Admin access denied"); }
  };
  useEffect(() => { load(); }, []);

  const toggleGating = async () => {
    if (!settings) return;
    const enabled = !settings.gating_enabled;
    const { data } = await api.put("/admin/beta-settings", { enabled });
    setSettings((s) => ({ ...s, gating_enabled: data.gating_enabled }));
    toast.success(enabled ? "Beta gating ON" : "Open registration ON");
  };

  const createInvites = async () => {
    setBusy(true);
    try {
      const emails = emailsInput.split(/[\s,;\n]+/).map((e) => e.trim()).filter(Boolean);
      const body = emails.length > 0 ? { count: emails.length, emails } : { count: Number(count) || 1 };
      const { data } = await api.post("/admin/invites", body);
      if (emails.length > 0) { toast.success(`Sent ${data.sent?.filter(s => s.message_id).length ?? 0} invite emails`); setEmailsInput(""); }
      else { toast.success(`${data.codes.length} codes created`); }
      await load();
    } finally { setBusy(false); }
  };

  const copyCode = (c) => { navigator.clipboard.writeText(c); toast.success("Copied"); };
  const iconFor = (kind) => kind === "bug" ? Bug : kind === "feature" ? Sparkles : kind === "rating" ? Star : MessageSquare;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="label-mono">// beta gating</div>
            <h2 className="mt-1 font-display text-xl font-bold flex items-center gap-2">
              <Ticket className="h-5 w-5 text-primary" /> {settings?.gating_enabled ? "Invite-only ON" : "Open registration"}
            </h2>
            {settings && <div className="mt-1 label-mono">{settings.used_count}/{settings.invite_count} used</div>}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={toggleGating} variant="outline" size="sm" className="rounded-full font-mono text-xs uppercase tracking-widest" data-testid="admin-toggle-gating-btn">
              {settings?.gating_enabled ? "Turn OFF" : "Turn ON"}
            </Button>
            <Input type="number" min="1" max="50" value={count} onChange={(e) => setCount(e.target.value)} className="w-20 h-10" />
            <Button data-testid={ADMIN.createInvitesBtn} onClick={createInvites} disabled={busy} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
              <Sparkles className="mr-2 h-4 w-4" /> {emailsInput.trim() ? "Send" : "Create"}
            </Button>
          </div>
        </div>
        <Input value={emailsInput} onChange={(e) => setEmailsInput(e.target.value)} placeholder="alice@example.com, bob@example.com — one code + email per address" className="h-10 font-mono text-sm mb-3" />
        {invites.invites?.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
            {invites.invites.slice(0, 20).map((v) => (
              <button key={v.code} onClick={() => copyCode(v.code)} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition ${v.used_by ? "border-border bg-muted/30 opacity-60" : "border-primary/40 bg-primary/5 hover:border-primary"}`}>
                <span className="font-mono text-sm">{v.code}</span>
                <span className="label-mono flex items-center gap-1">{v.used_by ? "used" : <><Copy className="h-3 w-3" /> copy</>}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section data-testid={ADMIN.feedbackList} className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <div className="label-mono">// feedback ({feedback.length})</div>
        </div>
        {feedback.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">No feedback yet.</div>
        ) : (
          <ul className="space-y-3">
            {feedback.map((f) => {
              const Icon = iconFor(f.kind);
              return (
                <li key={f.feedback_id} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 label-mono">
                        <span className="text-primary">{f.kind}</span>·<span className="truncate">{f.user_email}</span>
                        {f.page && <>·<span className="truncate">{f.page}</span></>}
                        {f.rating && <>·<span>{f.rating}/5</span></>}
                      </div>
                      <div className="mt-1 text-sm whitespace-pre-wrap">{f.message}</div>
                      <div className="mt-1 label-mono">{new Date(f.ts).toLocaleString()}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "learners", label: "Learners", icon: Users },
  { key: "feedback", label: "Feedback", icon: MessageSquare },
  { key: "ops", label: "Beta Ops", icon: Ticket },
  { key: "health", label: "System", icon: Activity },
];

export default function AdminPage() {
  const [tab, setTab] = useState("analytics");
  return (
    <div data-testid={ADMIN.root} className="space-y-6 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// admin · beta command center</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Operations.</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage learners, invites, and platform health.</p>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-widest transition ${tab === t.key ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "learners" && <LearnersTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "ops" && <OpsTab />}
      {tab === "health" && <HealthTab />}
    </div>
  );
}
